import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface ProgramDomain {
    id: string;
    name: string;
    description: string;
    status: string;
}

export interface ProgramDomainWritePayload {
    name?: string;
    description?: string;
    status?: string;
}

async function fetchProgramDomains(): Promise<ProgramDomain[]> {
    const res = await api.get<ProgramDomain[]>('/program-domains');
    return res.data;
}

export function useProgramDomains() {
    return useQuery({
        queryKey: ['program-domains'] as const,
        queryFn: fetchProgramDomains,
        staleTime: 60_000,
    });
}

export function useCreateProgramDomain() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ProgramDomainWritePayload) => {
            const res = await api.post<ProgramDomain>('/program-domains', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-domains'] }),
    });
}

export function useUpdateProgramDomain() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ProgramDomainWritePayload }) => {
            const res = await api.patch<ProgramDomain>(`/program-domains/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-domains'] }),
    });
}

export function useDeleteProgramDomain() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/program-domains/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['program-domains'] }),
    });
}
